import { deconstructPairId, getGeolocationKey, memoizeWithTimeout } from '../../utils.js';
import { SwapMode } from '../../common/types/index.js';
import { BASE_URL, DEFAULT_FEE_ADDRESS, DEFAULT_FEE_BPS, DEFAULT_SLIPPAGE, DEFAULT_SPENDER_ADDRESS, GAS_LIMIT_MULTIPLIER, MAX_AMOUNT_USD, MIN_ETH_AMOUNT_USD, MIN_EVM_AMOUNT_USD, NETWORK_TO_RANGO_BLOCKCHAIN, SVC, } from './constants.js';
import decodeTransaction, { getSpenderAddress, } from './utils/decode-transaction.js';
import { ETH_NULL_ADDRESS, LOGS, ZERO_ALLOWANCE_ASSETS } from '../../constants.js';
import { APPROVAL_GAS_LIMIT, EXACT_OUT_ALLOWANCE_THRESHOLD, QUOTE_CACHE_TTL } from '../constants.js';
import { isAssetPairExchangeable, parseFetchivalError, serializeQuoteKey } from '../utils.js';
import { CreateDexOrderError, CreateDexTxError, CreateTxError, ExchangeError, InvalidAmountError, InvalidFromTokenAddressError, InvalidToTokenAddressError, MissingFromAssetError, MissingPairError, MissingPairPriceError, MissingProviderOrderError, NoRateAvailableError, RangoProviderError, SendTxError, } from '../../errors.js';
import { simulateEvmTx } from '../simulation.js';
class Rango {
    name = 'rango';
    source = 'rango';
    _baseUrl;
    _fetchival;
    _feeAddress;
    _feeBps;
    _apiKeys;
    _apiKey;
    _assetsService;
    _gasPriceService;
    _ordersService;
    _pricingService;
    _cachedUsdPrices = new Map();
    _cachedQuotes = new Map();
    _tracer;
    constructor(params) {
        const { fetchival, apiKeys, feeAddress, feePercentage, ordersService, pricingService, assetsService, gasPriceService, baseUrl = BASE_URL, tracer, } = params;
        this._baseUrl = new URL(baseUrl);
        this._feeAddress = feeAddress || DEFAULT_FEE_ADDRESS;
        this._feeBps = feePercentage ? feePercentage * 100 : DEFAULT_FEE_BPS;
        this._apiKeys = apiKeys;
        this._fetchival = fetchival;
        this._assetsService = assetsService;
        this._gasPriceService = gasPriceService;
        this._ordersService = ordersService;
        this._pricingService = pricingService;
        this._apiKey = this._apiKeys.default;
        this._tracer = tracer;
    }
    async isPairSupported(pairId) {
        const { from, to } = deconstructPairId(pairId);
        const [fromAsset, toAsset, assetSources] = await Promise.all([
            this._assetsService.getOneById(from),
            this._assetsService.getOneById(to),
            this._assetsService.getAssetSources([this.source], [from, to]),
        ]);
        if (fromAsset?.network !== toAsset?.network) {
            return false;
        }
        return isAssetPairExchangeable({ from, to, assetSources });
    }
    async getQuote(params) {
        const { pairId, amount, fromAddress, slippage, swapMode = SwapMode.ExactIn } = params;
        const quoteCacheKey = serializeQuoteKey({ pairId, amount, fromAddress, slippage, swapMode });
        const cachedQuote = this._cachedQuotes.get(quoteCacheKey);
        if (!cachedQuote || Date.now() > cachedQuote.expiry) {
            const quote = await this._getQuote(params);
            this._cachedQuotes.set(quoteCacheKey, { quote, expiry: Date.now() + QUOTE_CACHE_TTL });
        }
        return this._cachedQuotes.get(quoteCacheKey).quote;
    }
    async _getQuote(params) {
        const { amount, getAllowance, slippage = DEFAULT_SLIPPAGE, swapMode = SwapMode.ExactIn, pairId, } = params;
        const { from, to } = deconstructPairId(pairId);
        const [fromAsset, toAsset, assetSources] = await Promise.all([
            this._assetsService.getOneById(from),
            this._assetsService.getOneById(to),
            this._assetsService.getAssetSources([this.source], [from, to]),
        ]);
        if (!fromAsset || !toAsset) {
            throw new MissingPairError({
                pairId,
                fromAsset,
                toAsset,
            });
        }
        if (!isAssetPairExchangeable({ from, to, assetSources })) {
            throw new Error(`Pair ${params.pairId} is not exchangeable`);
        }
        const isFeeAsset = !fromAsset[fromAsset.network];
        const rangoContracts = await this._getRangoContracts().catch(() => []);
        const rangoContract = rangoContracts.find((contract) => contract.blockchain === NETWORK_TO_RANGO_BLOCKCHAIN.get(fromAsset.network));
        const [response, limits, allowance] = await Promise.all([
            this._getQuoteFromRango({
                amount: params.amount,
                fromAsset,
                toAsset,
                swapMode,
                slippage,
            }),
            this.getLimits({ pairId: params.pairId }),
            isFeeAsset
                ? Promise.resolve(fromAsset.currency.defaultUnit(0))
                : getAllowance({
                    approvalAddress: rangoContract?.rangoContract || DEFAULT_SPENDER_ADDRESS,
                    assetId: fromAsset.id,
                }),
        ]);
        const limit = swapMode === SwapMode.ExactIn ? limits[0]?.ExactIn : limits[0]?.ExactOut;
        if (!limit || amount.gt(limit.max) || amount.lt(limit.min)) {
            const { value: minValue = 'UNDEF_VAL' } = limit?.min || {};
            const { value: maxValue = 'UNDEF_VAL' } = limit?.max || {};
            throw new NoRateAvailableError({
                amount: amount.toDefaultString(),
                max: maxValue.toString(),
                min: minValue.toString(),
            });
        }
        const routeFees = response.route.fee.find((fee) => fee.expenseType === 'FROM_SOURCE_WALLET');
        const gasLimit = Math.round(parseInt(routeFees?.meta?.gasLimit || '0') * GAS_LIMIT_MULTIPLIER) || 500_000;
        const shouldApproveTokens = !isFeeAsset && allowance.lt(params.amount);
        const shouldResetApprovalTokens = allowance.isPositive && ZERO_ALLOWANCE_ASSETS.includes(fromAsset.id);
        const approvalGasLimit = shouldApproveTokens
            ? shouldResetApprovalTokens
                ? 2 * APPROVAL_GAS_LIMIT
                : APPROVAL_GAS_LIMIT
            : 0;
        const quoteAmount = swapMode === SwapMode.ExactIn
            ? toAsset.currency.baseUnit(response.route.outputAmount)
            : fromAsset.currency.baseUnit(response.route.path[0]?.inputAmount);
        const rate = swapMode === SwapMode.ExactIn
            ? quoteAmount.div(params.amount.toDefaultString()).toDefaultString()
            : params.amount.div(quoteAmount.toDefaultString()).toDefaultString();
        return {
            providerName: this.name,
            rateId: response.requestId,
            amount: quoteAmount,
            rate,
            exchangeFeeData: {
                feeOpts: {
                    gasLimit: gasLimit + approvalGasLimit,
                },
            },
            exchangeTxData: {},
        };
    }
    async prepareExchange(params) {
        try {
            const { anonymousId, fromAddress, fromAmount, toAmount, onOrderIdGenerated, pairId, slippage = DEFAULT_SLIPPAGE, swapMode = SwapMode.ExactIn, toAddress, createTx, referralCode, quote, simulateTx, } = params;
            const { from, to } = deconstructPairId(params.pairId);
            this._traceMessage(LOGS.CREATING_ORDER);
            this._tracer?.traceTime('LogDexOrder');
            const orderPayload = {
                fromAmount: fromAmount.toDefaultString({ unit: false }),
                fromAddress,
                toAddress,
                toAmount: toAmount.toDefaultString({ unit: false }),
                pairId,
                slippage: slippage || DEFAULT_SLIPPAGE,
                svc: SVC,
                referralCode,
                swapMode,
            };
            const [fromAsset, toAsset, order] = await Promise.all([
                this._assetsService.getOneById(from),
                this._assetsService.getOneById(to),
                this._ordersService
                    .insertDex(orderPayload, { anonymousId })
                    .catch(async (err) => {
                    this._traceMessage(LOGS.FAILED_TO_CREATE_ORDER);
                    const errorMessage = await parseFetchivalError(err);
                    throw new CreateDexOrderError({
                        message: errorMessage,
                        details: orderPayload,
                    });
                })
                    .finally(() => this._tracer?.traceTimeEnd('LogDexOrder')),
            ]);
            if (typeof onOrderIdGenerated === 'function') {
                onOrderIdGenerated(order.id);
            }
            if (!fromAsset || !toAsset) {
                throw new MissingPairError({
                    pairId,
                    fromAsset,
                    toAsset,
                });
            }
            this._traceMessage(LOGS.GETTING_TX);
            this._tracer?.traceTime('GetDexSwapTx');
            const swapPayload = {
                apiKey: this._apiKey,
                amount: fromAmount.toBaseString({ unit: false }),
                from: this._getRangoIdForAsset(fromAsset),
                slippage: slippage || DEFAULT_SLIPPAGE,
                fromAddress,
                to: this._getRangoIdForAsset(toAsset),
                toAddress,
                referrerFee: this._feeBps / 100,
                referrerAddress: this._feeAddress,
                disableEstimate: true,
            };
            const response = await this._request('basic/swap')
                .get(swapPayload)
                .catch(async (err) => {
                this._traceMessage(LOGS.FAILED_TO_GET_TX);
                const errorMessage = await parseFetchivalError(err);
                throw new CreateDexTxError({
                    message: errorMessage,
                    details: swapPayload,
                    orderId: order.id,
                });
            })
                .finally(() => this._tracer?.traceTimeEnd('GetDexSwapTx'));
            if (response.error) {
                this._traceMessage(LOGS.FAILED_TO_GET_TX);
                throw new CreateDexTxError({
                    message: response.error || 'Unknown error from Rango API',
                    details: swapPayload,
                    orderId: order.id,
                });
            }
            this._traceMessage(LOGS.DECODING_TX);
            const transactionData = decodeTransaction(response.tx.txData, fromAddress);
            this._traceMessage(LOGS.CHECKING_ORDER_VALIDITY);
            this._tracer?.traceTime('CheckOrderIsValid');
            const orderIsValidPromise = this._checkOrderIsValid({ order, rangoTx: transactionData })
                .catch((err) => {
                this._traceMessage(LOGS.FAILED_TO_VALIDATE_ORDER);
                throw err;
            })
                .finally(() => this._tracer?.traceTimeEnd('CheckOrderIsValid'));
            const spenderAddress = response.tx.approveData && getSpenderAddress(response.tx.approveData, fromAddress);
            const simulationResultPromise = simulateEvmTx({
                data: {
                    baseAssetName: fromAsset.network,
                    swapTx: {
                        chainId: response.tx.blockChain.chainId,
                        from: response.tx.from,
                        to: response.tx.txTo,
                        gas: response.tx.gasLimit,
                        gasPrice: `0x${quote.exchangeFeeData.feeOpts?.gasPrice?.toBaseNumber().toString(16)}`,
                        value: response.tx.value || '0x0',
                        data: response.tx.txData,
                        nonce: undefined,
                        maxPriorityFeePerGas: response.tx.maxPriorityFeePerGas,
                        maxFeePerGas: response.tx.maxFeePerGas,
                    },
                    origin: this.name,
                    senderAddress: fromAddress,
                    approveToken: {
                        approveAmount: fromAmount,
                        assetId: fromAsset.id,
                        chainId: response.tx.blockChain.chainId,
                        spenderAddress,
                    },
                },
                simulate: simulateTx,
                tracer: this._tracer,
            });
            await Promise.all([simulationResultPromise, orderIsValidPromise]);
            this._traceMessage(LOGS.CREATING_TX);
            this._tracer?.traceTime('CreateTx');
            const createTxParams = {
                assetId: fromAsset.id,
                txData: {
                    chainId: Number(response.tx.blockChain.chainId),
                    data: response.tx.txData,
                    from: response.tx.from,
                    gasLimit: quote.exchangeFeeData.feeOpts.gasLimit,
                    gasPrice: quote.exchangeFeeData.feeOpts?.gasPrice,
                    tipGasPrice: quote.exchangeFeeData.feeOpts?.tipGasPrice,
                    to: response.tx.txTo,
                    value: response.tx.value,
                },
                isSendAll: params.isSendAll,
            };
            const transaction = await createTx(createTxParams)
                .catch((err) => {
                this._traceMessage(LOGS.FAILED_TO_CREATE_TX);
                throw new CreateTxError({
                    message: err.message,
                    details: createTxParams,
                    orderId: order.id,
                });
            })
                .finally(() => this._tracer?.traceTimeEnd('CreateTx'));
            const providerOrderId = response.requestId;
            return {
                approvalAddress: spenderAddress,
                providerOrderId,
                orderId: order.id,
                transaction,
                fromAmount: swapMode === SwapMode.ExactIn
                    ? fromAmount
                    : fromAmount.mul(1 + slippage / 100 + EXACT_OUT_ALLOWANCE_THRESHOLD),
            };
        }
        catch (err) {
            if (err instanceof ExchangeError) {
                throw err;
            }
            throw new ExchangeError({ message: err.message }, { cause: err });
        }
    }
    async executeExchange({ pairId, fromAmount, transaction, orderId, providerOrderId, approvalAddress, approveTokens, sendTx, }) {
        try {
            if (!providerOrderId) {
                throw new MissingProviderOrderError({
                    message: 'Missing providerOrderId',
                });
            }
            const { from } = deconstructPairId(pairId);
            const fromAsset = await this._assetsService.getOneById(from);
            if (!fromAsset) {
                throw new MissingFromAssetError({
                    fromAsset: from,
                });
            }
            const transactions = [];
            if (approvalAddress) {
                const approvalTxs = await approveTokens({
                    assetId: fromAsset.id,
                    approveAmount: fromAmount,
                    spenderAddress: approvalAddress,
                });
                transactions.push(...approvalTxs);
            }
            this._traceMessage(LOGS.SENDING_TX);
            this._tracer?.traceTime('SendTx');
            const { txId } = await sendTx(transaction)
                .catch((err) => {
                this._traceMessage(LOGS.FAILED_TO_SEND_TX);
                throw new SendTxError({
                    message: err.message,
                    details: transaction,
                });
            })
                .finally(() => this._tracer?.traceTimeEnd('SendTx'));
            transactions.push({ type: 'swapTransaction', id: txId });
            this._traceMessage(LOGS.UPDATING_ORDER);
            this._ordersService
                .updateDex({
                id: orderId,
                transactionId: txId,
                providerOrderId,
            })
                .catch((err) => console.warn(`Fail to update order ${orderId} with txId ${txId}`, err));
            return { transactions, svc: SVC, providerOrderId };
        }
        catch (err) {
            if (err instanceof ExchangeError) {
                throw err;
            }
            throw new ExchangeError({ message: err.message }, { cause: err });
        }
    }
    async getLimits(params) {
        try {
            const { from, to } = deconstructPairId(params.pairId);
            const [assetSources, fromAsset, toAsset, usdPrices] = await Promise.all([
                this._assetsService.getAssetSources([this.source], [from, to]),
                this._assetsService.getOneById(from),
                this._assetsService.getOneById(to),
                this._pricingService.getPrices([from, to]),
            ]);
            if (!isAssetPairExchangeable({ from, to, assetSources })) {
                throw new Error(`Pair ${params.pairId} is not exchangeable`);
            }
            if (!fromAsset || !toAsset) {
                throw new MissingPairError({
                    pairId: params.pairId,
                    fromAsset,
                    toAsset,
                });
            }
            const fromTokenUsdPrice = usdPrices[from];
            const toTokenUsdPrice = usdPrices[to];
            if (!fromTokenUsdPrice || !toTokenUsdPrice) {
                throw new MissingPairPriceError({
                    pairId: params.pairId,
                    fromAssetPrice: fromTokenUsdPrice,
                    toAssetPrice: toTokenUsdPrice,
                });
            }
            const minAmountUSD = fromAsset.network === 'ethereum' ? MIN_ETH_AMOUNT_USD : MIN_EVM_AMOUNT_USD;
            const exactInMinAmount = minAmountUSD / fromTokenUsdPrice;
            const exactInMaxAmount = MAX_AMOUNT_USD / fromTokenUsdPrice;
            const exactOutMinAmount = minAmountUSD / toTokenUsdPrice;
            const exactOutMaxAmount = MAX_AMOUNT_USD / toTokenUsdPrice;
            const minLimitBasedOnGasPrice = await this._gasPriceService
                .getMinUsdLimitBasedOnGasPrice(from)
                .catch(() => 0);
            const exactInMin = Math.max(exactInMinAmount, (minLimitBasedOnGasPrice || 0) / fromTokenUsdPrice);
            const exactOutMin = Math.max(exactOutMinAmount, (minLimitBasedOnGasPrice || 0) / toTokenUsdPrice);
            return [
                {
                    [SwapMode.ExactIn]: {
                        min: fromAsset.currency.defaultUnit(exactInMin),
                        max: fromAsset.currency.defaultUnit(exactInMaxAmount),
                    },
                    [SwapMode.ExactOut]: {
                        min: toAsset.currency.defaultUnit(exactOutMin),
                        max: toAsset.currency.defaultUnit(exactOutMaxAmount),
                    },
                },
            ];
        }
        catch {
            return [];
        }
    }
    setGeolocation(geolocation) {
        if (!geolocation) {
            this._apiKey = this._apiKeys.default;
            return;
        }
        const geolocationKey = getGeolocationKey(geolocation);
        if (this._apiKeys.hasOwnProperty(geolocationKey)) {
            this._apiKey = this._apiKeys[geolocationKey];
        }
    }
    _getRangoContracts = memoizeWithTimeout(async () => {
        const response = (await this._request('meta/rango-contracts').get({
            apiKey: this._apiKey,
        }));
        if (response.errorCode) {
            throw new Error('Getting Rango contracts failed ' + response.error);
        }
        return response.contracts;
    });
    _getQuoteFromRango = async (params) => {
        const { amount, fromAsset, toAsset, swapMode = SwapMode.ExactIn, slippage } = params;
        if (swapMode === SwapMode.ExactOut) {
            const limits = await this.getLimits({ pairId: `${fromAsset.id}_${toAsset.id}` });
            const fromAmount = limits[0].ExactIn.min;
            const quote = await this._getQuoteFromRango({
                ...params,
                amount: fromAmount,
                swapMode: SwapMode.ExactIn,
            });
            const toAmount = toAsset.currency.baseUnit(quote.route.outputAmount);
            const rate = toAmount.div(fromAmount.toDefaultString()).toDefaultString();
            const estimatedFromAmount = fromAsset.currency
                .defaultUnit(amount.div(rate).toDefaultString())
                .mul(1 + slippage / 100);
            return this._getQuoteFromRango({
                ...params,
                amount: estimatedFromAmount,
                swapMode: SwapMode.ExactIn,
            });
        }
        const baseAmount = amount.toBaseString({ unit: false });
        const rangoFromAsset = this._getRangoIdForAsset(fromAsset);
        const rangoToAsset = this._getRangoIdForAsset(toAsset);
        const response = await this._request('basic/quote').get({
            amount: baseAmount,
            apiKey: this._apiKey,
            from: rangoFromAsset,
            to: rangoToAsset,
            referrerFee: this._feeBps / 100,
            slippage,
        });
        if (response.resultType !== 'OK' || response.route.path.length === 0) {
            throw new RangoProviderError({ message: 'Getting quote from Rango API failed' });
        }
        return response;
    };
    _getRangoIdForAsset(asset) {
        const rangoBlockchain = NETWORK_TO_RANGO_BLOCKCHAIN.get(asset.network);
        if (!rangoBlockchain) {
            throw new RangoProviderError({ message: `missing blockchain for ${asset.network} network` });
        }
        if (asset.meta?.contractAddress) {
            return `${rangoBlockchain}.${asset.symbol}--${asset.meta.contractAddress}`;
        }
        return `${rangoBlockchain}.${asset.symbol}`;
    }
    async _checkOrderIsValid(params) {
        const { order, rangoTx } = params;
        const { from, to } = deconstructPairId(order.pairId);
        const fromAsset = await this._assetsService.getOneById(from);
        const toAsset = await this._assetsService.getOneById(to);
        if (!fromAsset || !toAsset) {
            throw new MissingPairError({
                pairId: order.pairId,
                fromAsset,
                toAsset,
            });
        }
        const fromTokenAddress = fromAsset.meta?.contractAddress || ETH_NULL_ADDRESS;
        const toTokenAddress = toAsset.meta?.contractAddress || ETH_NULL_ADDRESS;
        const amountFromOrder = fromAsset.currency
            .defaultUnit(order.amount.value)
            .toBaseString({ unit: false });
        if (amountFromOrder !== rangoTx.amount) {
            throw new InvalidAmountError({
                providerOrder: rangoTx,
                order,
                actual: rangoTx.amount,
                expected: amountFromOrder,
            });
        }
        if (rangoTx.receiver.toLowerCase() !== order.toAddress.toLowerCase()) {
            throw new InvalidToTokenAddressError({
                expected: order.toAddress,
                actual: rangoTx.receiver,
                order,
                providerOrder: rangoTx,
            });
        }
        if (rangoTx.fromToken.toLowerCase() !== fromTokenAddress.toLowerCase()) {
            throw new InvalidFromTokenAddressError({
                actual: rangoTx.fromToken,
                expected: fromTokenAddress,
                order,
                providerOrder: rangoTx,
            });
        }
        if (rangoTx.toToken.toLowerCase() !== toTokenAddress.toLowerCase()) {
            throw new InvalidToTokenAddressError({
                actual: rangoTx.toToken,
                expected: toTokenAddress,
                order,
                providerOrder: rangoTx,
            });
        }
    }
    _request(path) {
        const url = new URL(this._baseUrl);
        url.pathname = path;
        return this._fetchival(url);
    }
    _traceMessage(message) {
        this._tracer?.traceMessage(message);
    }
}
export default Rango;
